import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { StateValue } from 'xstate';
import { getApolloClient } from '../components/providers/graphql';
import { GetMeDocument, Provider } from '../generated/graphql';
import { MetaState } from '../redux/types';
import { FreshpaintUserIdType } from '../server/services/analytics/service';
import { AssessmentStepProperties } from '../types/window';
import { isWindowPropertyAvailable } from './window';

export type AssessmentEventName =
  | 'SubmitApplication'
  | 'ViewContent'
  | 'Lead'
  | 'Non-PHI Assessment step';

// This tracks terminate assessment and submit assessment question
export function trackEvent(
  action: string,
  event_category: string,
  event_label: string
) {
  if (isWindowPropertyAvailable('gtag')) {
    window.gtag('event', action, {
      event_category,
      event_label,
    });
  }
}

function triggerIframeMessageThirdPartyAdvertising(
  eventName: AssessmentEventName,
  payload?: Object
) {
  document
    .querySelectorAll('.analyticsSandbox')
    .forEach((it: HTMLIFrameElement) =>
      it?.contentWindow?.postMessage?.({ eventName, payload }, '/')
    );
}

/**
 * Tracks the completion of the assessment, regardless if it was a successful submission
 * or the result of termination.
 * Note: Once the xstate codepath is removed, this function should not be exported.
 * @param event_label Label corresponding to the step of the assessment
 */
export function trackCompletedAssessment(event_label: string): void {
  trackEvent('Submit', 'Complete assessment', event_label); // GA
  if (isWindowPropertyAvailable('freshpaint')) {
    window.freshpaint.track('Completed assessment');
  }
}

/**
 * Tracks the termination within the assessment.
 * @param event_label Label corresponding to the step of the assessment
 */
export function trackTerminatedAssessment(event_label: string): void {
  trackCompletedAssessment(event_label);
  trackEvent('Submit', 'Terminate assessment', event_label); // GA
  if (isWindowPropertyAvailable('freshpaint')) {
    window.freshpaint.track('Terminate assessment');
  }
}

/**
 * Tracks the successful submission of an assessment.
 * @param event_label Label corresponding to the step of the assessment
 */
export function trackSuccessfulCompletedAssessment(event_label: string): void {
  trackCompletedAssessment(event_label);
  triggerIframeMessageThirdPartyAdvertising('SubmitApplication');
  if (isWindowPropertyAvailable('freshpaint')) {
    window.freshpaint.track('Successful completed assessment');
  }
}

export async function identifyForAnalytics(
  sessionPatientId?: string
): Promise<void> {
  let fetchedSessionPatientId;
  const { data } = await getApolloClient().query({
    query: GetMeDocument,
  });

  if (data?.getMe?.id) {
    fetchedSessionPatientId = data.getMe.id;
  } else {
    return;
  }

  if (isWindowPropertyAvailable('freshpaint')) {
    window.freshpaint.track('Identity', {
      sessionPatientId: sessionPatientId ?? fetchedSessionPatientId,
      idType: FreshpaintUserIdType.SESSION_PATIENT_ID,
    });
  }
}

export function trackPageView(page_title?: string, page_location?: string) {
  if (isWindowPropertyAvailable('gtag')) {
    window.gtag('event', 'page_view', {
      page_title: page_title ?? document.title,
      page_location: page_location ?? window.location.href,
    });
  }
}

function resetFreshpaintCustomEventProperties() {
  window.freshpaint.removeEventProperty('questionCode');
  window.freshpaint.removeEventProperty('subTitle');
  window.freshpaint.removeEventProperty('title');
}

export function trackFetchingMatches(): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();
    const payload = {
      title: 'matching',
      subTitle: 'fetchingMatches',
    };
    window.freshpaint.track('Assessment step', payload);
  }
}

export function trackMenuClick(menuState: boolean): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();
    const payload = {
      menuState: menuState ? 'open' : 'closed',
    };
    window.freshpaint.track('Menu click', payload);
  }
}

let introVideoLoadStateMark: PerformanceEntry = null;

export function trackVideoPlayback(videoState: string): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();

    window.freshpaint.track('Intro 1 Video click: Get to know Talkiatry', {
      videoState,
    });
  }

  if (videoState === 'loading') {
    introVideoLoadStateMark = performance.mark(
      'assessment__intro_video__loading'
    );
  } else if (videoState === 'loaded') {
    const measurement = performance.measure(
      'assessment__intro_video__first_load',
      'assessment__intro_video__loading'
    );
    console.log({
      action: 'assessment__intro_video__first_load',
      duration: measurement.duration,
      start_time: introVideoLoadStateMark?.startTime,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      success: true,
    });
    if (process.env.NEXT_PUBLIC_DD_ENV === 'prod') {
      performance.clearMarks('assessment__intro_video__loading');
      performance.clearMeasures('assessment__intro_video__first_load');
    }
  }
}

function trackMatchesDisplayedHelper(
  matchesDisplayed: Provider[],
  eventName: string
): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();

    const providerIds: string[] = [];
    const earliestAvailableTimeslots: number[] = [];

    matchesDisplayed.forEach(m => {
      providerIds.push(m.id);
      earliestAvailableTimeslots.push(
        new Date(m.providerTimeslots[0].startTime).getTime()
      );
    });

    window.freshpaint.track(eventName, {
      numMatchesDisplayed: matchesDisplayed.length,
      providerIds,
      earliestAvailableTimeslots,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }
}

export function trackSeeMoreMatchesClick(matchesDisplayed: Provider[]): void {
  trackMatchesDisplayedHelper(
    matchesDisplayed,
    'Patient: Matching - See more matches click'
  );
}

export function trackTopMatchesDisplayed(matchesDisplayed: Provider[]): void {
  trackMatchesDisplayedHelper(
    matchesDisplayed,
    'Patient: Matching - Top matches displayed'
  );
}

export function trackAdditionalMatchSelectedOrBooked(
  providerId: string,
  action: 'selected' | 'booked'
): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();

    window.freshpaint.track(`Additional match ${action}`, {
      providerId,
    });
  }
}

export function trackSchedulingTimeslotsAvailableWhenBooking(
  availableStartTimes: string[],
  providerId: string,
  scheduledStartTime: string
): void {
  if (isWindowPropertyAvailable('freshpaint')) {
    resetFreshpaintCustomEventProperties();
    window.freshpaint.track(
      'Patient: Scheduling - Timeslots Available When Booking',
      {
        availableStartTimes,
        providerId,
        scheduledStartTime,
      }
    );
  }
}

/**
 * This function is solely used to make Freshpaint event title, subTitle, and questionCode
 * values the same when transitioning from XState to Redux. In XState, these values were:
 *
 * title: Assessment machine state (i.e., verifyEmail, demographics)
 * subTitle: Assessment submachine state (i.e., question, conditionsQuestions)
 * questionCode: submachine sub-state (i.e., current questionCode)
 * @param trackingPayload the object where the determined tracking payload will be store
 * @param meta the redux state's present meta
 */
const formatReduxTrackingPayload = (trackingPayload, meta: MetaState) => {
  const { screen, section, step: isQuestion } = meta;

  if (!section) {
    trackingPayload.title = screen;
    return;
  }
  trackingPayload.title = section;

  if (isQuestion) {
    trackingPayload.subTitle = 'question';
    trackingPayload.questionCode = screen;
    return;
  }
  trackingPayload.subTitle = screen;
};

const getEventLabelFromTrackingPayload = (
  trackingPayload: AssessmentStepProperties
) => {
  const { title, subTitle, questionCode } = trackingPayload;
  let current = title;
  if (subTitle) current += `.${subTitle}`;
  if (questionCode) current += `.${questionCode}`;
  return current;
};

export const getEventLabelFromMeta = (meta: MetaState) => {
  const trackingPayload = {};
  formatReduxTrackingPayload(trackingPayload, meta);
  return getEventLabelFromTrackingPayload(trackingPayload);
};

export function useAssessmentAnalytics(
  currentValue: MetaState | StateValue,
  stepTitleIsNotPHI?: boolean
): void {
  const [lastTrackingPayload, setLastTrackingPayload] =
    useState<AssessmentStepProperties>({});

  useEffect(() => () => resetFreshpaintCustomEventProperties(), []);

  // Freshpaint
  useEffect(() => {
    if (!isWindowPropertyAvailable('freshpaint')) return;

    const trackingPayload: AssessmentStepProperties = {};

    formatReduxTrackingPayload(trackingPayload, currentValue as MetaState);

    if (!isEqual(lastTrackingPayload, trackingPayload)) {
      resetFreshpaintCustomEventProperties();
      window.freshpaint.track('Assessment step', trackingPayload);
      if (stepTitleIsNotPHI) {
        triggerIframeMessageThirdPartyAdvertising(
          'ViewContent',
          trackingPayload
        );
        triggerIframeMessageThirdPartyAdvertising(
          'Non-PHI Assessment step',
          trackingPayload
        );
        window.freshpaint.track('Non-PHI Assessment step', trackingPayload);
        window.freshpaint.addEventProperties(trackingPayload);
      }
      if (trackingPayload.questionCode === 'patientType') {
        triggerIframeMessageThirdPartyAdvertising('Lead');
        window.freshpaint.track('Email authenticated', trackingPayload);
      }
      setLastTrackingPayload(trackingPayload);
    }
  }, [currentValue]);

  // Google Universal Analytics (redux-only)
  useEffect(() => {
    const eventLabel = getEventLabelFromMeta(currentValue as MetaState);

    trackPageView(`${document.title}: ${eventLabel}`);
    trackEvent('Submit', 'Answer assessment question', eventLabel); // this copies "sendSubmitAssessmentQuestionAnalyticsEvent" for XState which was in the pages/assessmentt.tsx
  }, [currentValue]);
}
